import { useEffect, useRef, useState } from "react";

const CALENDAR_ID = "pxEd1mP4powsfvCv0FZA";
const EMBED_SCRIPT_SRC = "https://link.msgsndr.com/js/form_embed.js";

/**
 * Lazy-mounts the GoHighLevel booking widget only when it scrolls near the viewport,
 * or when the user explicitly clicks the placeholder. Uses GHL's form_embed.js
 * script so the widget auto-resizes inline rather than being a fixed-height iframe.
 */
export function LazyBookingIframe() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;
    if (document.querySelector(`script[src="${EMBED_SCRIPT_SRC}"]`)) return;
    const s = document.createElement("script");
    s.src = EMBED_SCRIPT_SRC;
    s.async = true;
    document.body.appendChild(s);
  }, [shouldLoad]);

  return (
    <div ref={containerRef} className="min-h-[750px] w-full">
      {shouldLoad ? (
        <iframe
          src={`https://api.leadconnectorhq.com/widget/booking/${CALENDAR_ID}`}
          style={{ width: "100%", border: "none", overflow: "hidden" }}
          scrolling="no"
          id={`${CALENDAR_ID}_${Date.now()}`}
          title="Schedule Consultation"
          className="w-full"
        />
      ) : (
        <button
          type="button"
          data-ga-event="booking_click"
          onClick={() => setShouldLoad(true)}
          className="flex min-h-[750px] w-full flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white text-[#0b0f3d] shadow-sm transition hover:bg-slate-50"
          aria-label="Load the consultation booking calendar"
        >
          <span className="text-lg font-semibold">Loading booking calendar…</span>
          <span className="mt-2 text-sm text-slate-600">Tap to open now</span>
        </button>
      )}
    </div>
  );
}
